import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Wind } from "lucide-react";
import SoulHero from "@/components/soul/SoulHero";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import N8Emblem from "@/components/N8Emblem";
import { BREATH_PRACTICES } from "@/pages/soul/breath/breathContent";
import { HERO } from "@/lib/heroImages";

/**
 * BreathIndex — a quiet, numbered editorial list of Breath practices.
 * Chosen over a large card grid so the "eight vs five vs seven" count
 * is never structural — practices can be added or removed without the
 * page feeling different.
 */
export default function BreathIndex() {
  const nav = useNavigate();
  return (
    <div className="space-y-12" data-testid="soul-breath-index">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul", to: "/soul" },
            { label: "Regulation & Presence", to: "/soul/regulation-presence" },
            { label: "Breath" },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/soul/regulation-presence")}
          data-testid="soul-breath-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <ArrowLeft size={12} /> Back to Regulation & Presence
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow="Soul · Regulation & Presence"
        title="Breath"
        supporting="Return to the rhythm that is always with you."
        body="A small set of breath practices — each a quiet doorway back to the present. Choose the one that meets you today."
        image={HERO.breathIndex}
        testid="soul-breath-hero"
      />

      <section
        className="reveal reveal-1"
        data-testid="soul-breath-list"
      >
        <ul className="divide-y divide-[hsl(var(--line))] border-y border-[hsl(var(--line))]">
          {BREATH_PRACTICES.map((p, i) => (
            <li key={p.slug}>
              <Link
                to={`/soul/regulation-presence/breath/${p.slug}`}
                data-testid={`soul-breath-item-${p.slug}`}
                className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-5 md:gap-8 py-6 md:py-8 transition-colors hover:bg-[hsl(var(--sand))]/30 px-2 md:px-4"
              >
                <span className="font-display text-2xl md:text-3xl italic text-[hsl(var(--copper))] tabular-nums w-10 md:w-14">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[hsl(var(--fg))]">
                      {p.title}
                    </h3>
                    <span className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                      {p.subtitle}
                    </span>
                  </div>
                  <p className="mt-2 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80 max-w-2xl">
                    {p.tagline}
                  </p>
                </div>
                <span className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] whitespace-nowrap">
                  Explore <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Safety copy — never buried, always present */}
      <section
        className="p-6 md:p-8 rounded-[20px] border hairline bg-[hsl(var(--sand))]/40 max-w-3xl"
        data-testid="soul-breath-safety"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] inline-flex items-center gap-2">
          <Wind size={12} /> A gentle note
        </div>
        <p className="mt-3 text-[14.5px] leading-relaxed text-[hsl(var(--fg))]/80">
          These practices are invitations, not prescriptions. Never force the breath. If you feel dizzy,
          lightheaded, or uncomfortable at any point, return to your normal breathing and rest. These
          practices do not treat or replace care for medical or mental-health conditions — if any apply
          to you, please practice under the guidance of a qualified professional.
        </p>
      </section>
    </div>
  );
}
